/* eslint-disable react/no-unknown-property */
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Action, ActionIconDropdown, Avatar, Dropdown, FontWeight, HStack, ICON_CELLPHONE,
  Text,
  Tooltip,
  useNotifications,
} from '@doveit/bricks';
import { formatDate, pluralizeWord } from '@doveit/hammer';
import { CallCenterContactPageType, PageType } from '../../types';
import RightColumnPageLayout from '../../../layouts/right-column-page-layout/RightColumnPageLayout';
import useContact from '../../../contact/hooks/use-contact/useContact';
import GenericErrorPage from '../../../pages/errors/generic/GenericErrorPage';
import ContactWidget from '../../../contact/components/contact-widget/ContactWidget';
import useRBAC from '../../../hooks/use-rbac/useRBAC';
import AircallButton from '../../../containers/aircall-button/AircallButton';
import { ReferenceType } from '../../../domain/types';
import {
  Call, CallContextType, Contact, Note, ProcessableContact,
} from '../../../providers/api/dtos';
import buildMobileCall from '../../../utils/call/call';
import { createCall } from '../../../providers/api/call/callProvider';
import useFilters, { Filters } from '../../../hooks/use-filters/useFilters';
import ViewContactToProcessCount from '../../containers/view-contact-to-process-count/ViewContactToProcessCount';
import ExitSession from '../../../components/exit-session/ExitSession';
import NoAnswerAction from '../../containers/no-answer-action/NoAnswerAction';
import EventRegistryWidget from '../../containers/event-registry-widget/EventRegistryWidget';
import { NO_VALUE_SYMBOL } from '../../../property/utils';
import { goToSearchPage } from '../../../utils/navigate/utils';
import ContactIntentsWidget from '../../../contact/containers/contact-intents-widget/ContactIntentsWidget';
import RightColumnPageSkeleton from '../../../components/page-skeleton/RightColumnPageSkeleton';
import ContactRumorsWidget from '../../../contact/containers/contact-rumors-widget/ContactRumorsWidget';
import ContactLeadsWidget from '../../../contact/containers/contact-leads-widget/ContactLeadsWidget';
import { updateContact } from '../../../providers/api/contact/contactProvider';
import useIsDevice from '../../../hooks/use-is-device/useIsDevice';
import { useContactToProcessNoticesData } from '../../../contact/hooks/use-contact-to-process-notices-data/useContactToProcessNoticesData';
import { buildContactToProcessNotice } from '../../../utils/notice/buildContactNotice';
import NoticeBoard from '../../../notice/components/notice-board/NoticeBoard';
import NotesSection from '../../../notes/components/notes-section/NotesSection';
import FeedbacksWidget from '../../../feedback/containers/feedbacks-widget/FeedbacksWidget';
import NextContactToProcessAction from '../../containers/next-contact-to-process-action/NextContactToProcessAction';
import CallBackAction from '../../containers/call-back-action/CallBackAction';
import CloseCallAction from './CloseCallAction';
import { UseTimerResult } from '../../../hooks/use-timer/useTimer';
import useUserPermissions from '../../../hooks/use-user-permissions/useUserPermissions';

export const LOAD_PAGE_DATA_ERROR_MESSAGE = 'Non è stato possibile caricare i dati della pagina.';
export const UPDATE_CONTACT_SUCCESS_MESSAGE = 'Il contatto è stato modificato con successo.';
export const UPDATE_CONTACT_ERROR_MESSAGE = 'Non è stato possibile modificare il contatto.';
export const NEXT_CONTACT_ERROR_MESSAGE = 'Impossibile caricare il prossimo contatto da processare.';
export const CALL_EVENT_ERROR_MESSAGE = 'Impossibile salvare le informazioni della chiamata.';

const pageToCallContextType: Record<CallCenterContactPageType, CallContextType> = {
  [PageType.PROCESS_CONTACT_LEADS]: CallContextType.LEAD,
  [PageType.PROCESS_CONTACT_COLD_LEADS]: CallContextType.COLD_LEAD,
  [PageType.PROCESS_CONTACT_SURVEYS]: CallContextType.SURVEY,
  [PageType.DETAIL]: CallContextType.NONE,
};

interface CreateIntentPageFilters extends Filters {
  correlationId?: string;
}

export interface CallCenterContactPageProps {
  type: CallCenterContactPageType,
}

export type CallCenterComponentsType = 'lead' | 'cold-lead' | 'survey';

const CallCenterView: React.FC<CallCenterContactPageProps> = ({
  type,
}) => {
  const { id: contactId } = useParams();
  const { filters: { correlationId } } = useFilters<CreateIntentPageFilters>();
  const { user, userIsCallCenter, userIsAdmin } = useRBAC();
  const navigate = useNavigate();
  const [showCallControls, setShowCallControls] = React.useState<boolean>(false);
  const [disableNextContact, setDisableNextContact] = React.useState<boolean>(true);
  const [disableControls, setDisableControls] = React.useState<boolean>(false);
  const [showControls, setShowControls] = React.useState<boolean>(false);
  const [showStopCall, setShowStopCall] = React.useState<boolean>(false);
  const [callEvent, setCallEvent] = React.useState<Call | undefined>();
  const { addSuccess, addError } = useNotifications();
  const closeCallActionRef = React.useRef<UseTimerResult>(null);
  const userPermissions = useUserPermissions(['contacts', 'contactServices', 'contactOptIns', 'contactConcessions']);

  const priorityComponents: Record<CallCenterContactPageType, CallCenterComponentsType[]> = React.useMemo(() => ({
    [PageType.PROCESS_CONTACT_LEADS]: ['lead', 'cold-lead', 'survey'],
    [PageType.PROCESS_CONTACT_COLD_LEADS]: ['cold-lead', 'lead', 'survey'],
    [PageType.PROCESS_CONTACT_SURVEYS]: ['survey', 'lead', 'cold-lead'],
    [PageType.DETAIL]: ['lead', 'cold-lead'],
  }), []);

  const isMobile = useIsDevice('mobile');

  React.useEffect(() => {
    setDisableNextContact(true);
    setShowControls(false);
    setShowStopCall(false);
  }, [contactId]);

  React.useEffect(() => {
    if (showStopCall) {
      closeCallActionRef.current?.start();
    }
  }, [showStopCall]);

  const {
    data: contact,
    isLoading: isContactLoading,
    error: contactError,
    mutate: mutateContact,
  } = useContact(contactId);

  const { data: contactToProcessNoticesData = [], mutate: mutateContactToProcessNoticesData } = useContactToProcessNoticesData(contactId);
  const contactToProcessNotices = React.useMemo(() => contactToProcessNoticesData.map((noticeData) => buildContactToProcessNotice(noticeData, {
    isMobile,
    onLeadUpdate: mutateContactToProcessNoticesData,
  })), [contactToProcessNoticesData, isMobile, mutateContactToProcessNoticesData]);

  const onContextTypeDataLoaded = React.useCallback(() => {
    setShowCallControls(true);
  }, []);

  const canSeeFeedback = userIsCallCenter || userIsAdmin;
  const canUpdateFeedbackStatus = userIsCallCenter || userIsAdmin;

  const onContactUpdate = React.useCallback(async () => {
    await mutateContact();
  }, [mutateContact]);

  const onLeadUpdate = React.useCallback(async () => {
    setDisableNextContact(false);
  }, []);

  const onFeedbackUpdate = React.useCallback(async () => {
    setDisableNextContact(false);
  }, []);

  const onCallStarted = React.useCallback(() => {
    setShowCallControls(false);
    setShowControls(true);
  }, []);

  const onCallMobileStarted = React.useCallback(() => {
    setShowCallControls(false);
    setShowStopCall(true);
  }, []);

  const stopCall = React.useCallback(() => {
    if (closeCallActionRef.current?.isActive) {
      closeCallActionRef.current?.stop();

      const event = buildMobileCall({
        outboundNumber: contact!.phoneNumber!,
        duration: closeCallActionRef.current?.seconds,
        leadTransformationSpecialist: user!.email!,
        context: {
          type: pageToCallContextType[type],
          id: contact!.id!,
        },
      });

      closeCallActionRef.current?.reset();
      setCallEvent(event);

      return event;
    }

    return callEvent;
  }, [closeCallActionRef, callEvent, contact, user, type]);

  const sendCallEvent = React.useCallback(async (event: Call | undefined) => {
    if (event) {
      try {
        await createCall({
          ...event,
          correlationId,
        });

        setCallEvent(undefined);
      } catch (err) {
        addError(CALL_EVENT_ERROR_MESSAGE);
      }
    }
  }, [addError, correlationId]);

  const onNoAnswerSuccess = React.useCallback(async () => {
    if (callEvent) {
      const updatedCallEvent = { ...callEvent!, answered: false };
      setCallEvent(updatedCallEvent);

      await sendCallEvent(updatedCallEvent);
    }

    if (type === PageType.DETAIL) {
      setShowControls(false);
      setShowCallControls(true);
    }

    setDisableNextContact(false);
  }, [sendCallEvent, callEvent, type]);

  const onCallFinished = React.useCallback(() => {
    stopCall();
    setShowStopCall(false);
    setShowControls(true);
  }, [stopCall]);

  const onCallBackSuccess = React.useCallback(async (dateTime: string) => {
    setDisableNextContact(false);
    addSuccess(`Il contatto verrà richiamato il giorno ${formatDate(new Date(dateTime), {
      time: true,
    })}`);

    await sendCallEvent(callEvent ? { ...callEvent, answered: true } : undefined);

    if (type === PageType.DETAIL) {
      setShowControls(false);
      setShowCallControls(true);
    }
  }, [addSuccess, sendCallEvent, callEvent, type]);

  const onCallBackError = React.useCallback(() => {
    setDisableNextContact(false);
    sendCallEvent(callEvent);
  }, [callEvent, sendCallEvent]);

  const onNextLeadContactSuccess = React.useCallback(async (processableContact?: ProcessableContact) => {
    await sendCallEvent(stopCall());

    if (!processableContact) {
      navigate('/call-center/contacts/process', { replace: true });
      return;
    }

    navigate(`/call-center/contacts/process/${processableContact.id}/leads?correlation_id=${processableContact.correlationId}`, { replace: true });
  }, [sendCallEvent, stopCall, navigate]);

  const onNextLeadContactError = React.useCallback(async () => {
    await sendCallEvent(stopCall());

    setDisableControls(false);
    addError(NEXT_CONTACT_ERROR_MESSAGE);
  }, [addError, stopCall, sendCallEvent]);

  const onNextColdLeadContactSuccess = React.useCallback(async (processableContact?: ProcessableContact) => {
    await sendCallEvent(stopCall());

    if (!processableContact) {
      navigate('/call-center/contacts/process', { replace: true });
      return;
    }

    navigate(`/call-center/contacts/process/${processableContact.id}/cold-leads?correlation_id=${processableContact.correlationId}`, { replace: true });
  }, [sendCallEvent, stopCall, navigate]);

  const onNextColdLeadContactError = React.useCallback(async () => {
    await sendCallEvent(stopCall());

    setDisableControls(false);
    addError(NEXT_CONTACT_ERROR_MESSAGE);
  }, [addError, stopCall, sendCallEvent]);

  const onNextFeedbackContactSuccess = React.useCallback(async (processableContact?: ProcessableContact) => {
    await sendCallEvent(stopCall());

    navigate(`/call-center/contacts/process/${processableContact?.id}/surveys`, { replace: true });
  }, [navigate, sendCallEvent, stopCall]);

  const onNextFeedbackContactError = React.useCallback(async () => {
    await sendCallEvent(stopCall());

    setDisableControls(false);
    addError(NEXT_CONTACT_ERROR_MESSAGE);
  }, [addError, sendCallEvent, stopCall]);

  const onNextContact = React.useMemo(() => {
    switch (type) {
      case PageType.PROCESS_CONTACT_LEADS:
        return { success: onNextLeadContactSuccess, error: onNextLeadContactError };
      case PageType.PROCESS_CONTACT_COLD_LEADS:
        return { success: onNextColdLeadContactSuccess, error: onNextColdLeadContactError };
      case PageType.PROCESS_CONTACT_SURVEYS:
        return { success: onNextFeedbackContactSuccess, error: onNextFeedbackContactError };
      default:
        return undefined;
    }
  }, [onNextColdLeadContactError, onNextColdLeadContactSuccess, onNextFeedbackContactError, onNextFeedbackContactSuccess, onNextLeadContactError, onNextLeadContactSuccess, type]);

  const onExit = React.useCallback(() => {
    sendCallEvent(stopCall());
    navigate('/call-center/contacts/process');
  }, [navigate, sendCallEvent, stopCall]);

  const openSearchContactPage = React.useCallback(() => {
    goToSearchPage(ReferenceType.CONTACT);
  }, []);

  const handleCallCenterNotesUpdate = React.useCallback(async (callCenterNotes: Note[]) => {
    if (!contact) return;

    try {
      const contactToUpdate: Contact = {
        ...contact,
        callCenterNotes,
      };

      await updateContact(contact.id!, contactToUpdate);

      addSuccess(UPDATE_CONTACT_SUCCESS_MESSAGE);

      await mutateContact();
    } catch (err) {
      addError(UPDATE_CONTACT_ERROR_MESSAGE);
    }
  }, [addError, addSuccess, contact, mutateContact]);

  if (contactError) {
    return <GenericErrorPage title={LOAD_PAGE_DATA_ERROR_MESSAGE} />;
  }

  if (isContactLoading) {
    return <RightColumnPageSkeleton />;
  }

  return contact! && (
    <RightColumnPageLayout
      aria-label="Pagina processo call center"
      title={(
        <HStack gap={200}>
          <Avatar />
          <div>
            <div>
              <Text.H4
                fontWeight={FontWeight.REGULAR}
                as="div"
              >
                {contact.name || NO_VALUE_SYMBOL}
              </Text.H4>
            </div>
            <Text.Body as="div">
              {contact.phoneNumber}
            </Text.Body>
          </div>
        </HStack>
      )}
      actions={(
        <ActionIconDropdown
          label="Azioni pagina contatto"
          aria-label="Azioni pagina contatto"
        >
          <Dropdown.Option
            label="Cerca contatto"
            aria-label="Apri la pagina di ricerca contatto"
            onClick={openSearchContactPage}
          />
        </ActionIconDropdown>
      )}
      topContent={<NoticeBoard notices={contactToProcessNotices} />}
      sidePrimaryContent={(
        <ContactWidget
          contact={contact}
          canViewContact={userPermissions.contacts.canSee}
          canEditContact={userPermissions.contacts.canEdit()}
          canSeeAdditionalServices={userPermissions.contactServices.canSee}
          canAddAdditionalServices={userPermissions.contactServices.canAdd()}
          canDeleteAdditionalServices={userPermissions.contactServices.canDelete()}
          canSeeConcessions={userPermissions.contactConcessions.canSee}
          canAddConcessions={userPermissions.contactConcessions.canAdd()}
          canDeleteConcessions={userPermissions.contactConcessions.canDelete()}
          canSeeOptIns={userPermissions.contactOptIns.canSee}
          canAddOptIns={userPermissions.contactOptIns.canAdd()}
          canDeleteOptIns={userPermissions.contactOptIns.canDelete()}
          onUpdate={onContactUpdate}
        />
      )}
      sideSecondaryContent={
        contact?.phoneNumber && (<EventRegistryWidget phoneNumber={contact.phoneNumber} />)
      }
      footer={(
        <HStack alignItems="center" justifyContent="space-between">
          <HStack>
            {showCallControls && (
              <>
                <AircallButton
                  phoneNumber={contact.phoneNumber!}
                  onSuccess={onCallStarted}
                />
                <Action
                  label="Mobile"
                  aria-label="Azione per avviare la chiamata con mobile"
                  iconLeft={{ path: ICON_CELLPHONE }}
                  color="primary"
                  onClick={onCallMobileStarted}
                />
              </>
            )}
            {showStopCall && (
              <CloseCallAction
                actionRef={closeCallActionRef}
                onCallFinished={onCallFinished}
              />
            )}
            {showControls && (
              <>
                <CallBackAction
                  type={pageToCallContextType[type]}
                  contactId={contactId!}
                  onSuccess={onCallBackSuccess}
                  onError={onCallBackError}
                  disabled={disableControls}
                />
                <NoAnswerAction
                  type={pageToCallContextType[type]}
                  contactId={contactId!}
                  onSuccess={onNoAnswerSuccess}
                  onError={onNoAnswerSuccess}
                  disabled={disableControls}
                />
              </>
            )}
          </HStack>

          {type !== PageType.DETAIL && (
            <HStack>
              <NextContactToProcessAction
                type={pageToCallContextType[type]}
                disabled={disableNextContact}
                onSuccess={onNextContact?.success}
                onError={onNextContact?.error}
              />
              <ViewContactToProcessCount type={pageToCallContextType[type]}>
                {(count) => (
                  <Tooltip content={`${count} ${pluralizeWord('contatt', 'o', 'i', count)} da processare`}>
                    <div>
                      <ExitSession
                        badgeLabel={count}
                        onExit={onExit}
                      />
                    </div>
                  </Tooltip>
                )}
              </ViewContactToProcessCount>
            </HStack>
          )}
        </HStack>
      )}
    >
      {
        priorityComponents[type].map((componentType) => {
          switch (componentType) {
            case 'lead':
              return (
                <ContactLeadsWidget
                  key={componentType}
                  contactId={contact.id!}
                  type="HOT"
                  onLeadUpdate={onLeadUpdate}
                  onLeadsLoaded={onContextTypeDataLoaded}
                />
              );
            case 'cold-lead':
              return (
                <ContactLeadsWidget
                  key={componentType}
                  contactId={contact.id!}
                  type="COLD"
                  onLeadUpdate={onLeadUpdate}
                  onLeadsLoaded={onContextTypeDataLoaded}
                />
              );
            case 'survey':
              return (
                <FeedbacksWidget
                  key={componentType}
                  filters={{
                    contactId,
                  }}
                  canSeeFeedback={canSeeFeedback}
                  canUpdateStatus={canUpdateFeedbackStatus}
                  onFeedbacksLoaded={onContextTypeDataLoaded}
                  onFeedbackUpdated={onFeedbackUpdate}
                />
              );
            default: return null;
          }
        })
      }
      <NotesSection
        notes={contact.callCenterNotes}
        onNotesUpdate={handleCallCenterNotesUpdate}
      />
      <ContactRumorsWidget contactId={contact.id!} />
      <ContactIntentsWidget contactId={contact.id!} />
      {type === PageType.DETAIL && (
        <FeedbacksWidget
          filters={{
            contactId,
          }}
          canSeeFeedback={canSeeFeedback}
          canUpdateStatus={canUpdateFeedbackStatus}
          onFeedbacksLoaded={onContextTypeDataLoaded}
          onFeedbackUpdated={onFeedbackUpdate}
        />
      )}
    </RightColumnPageLayout>
  );
};

export function CallCenterContactProcessLeadsDetailPage() {
  return (
    <CallCenterView type={PageType.PROCESS_CONTACT_LEADS} />
  );
}

export function CallCenterContactProcessColdLeadsDetailPage() {
  return (
    <CallCenterView type={PageType.PROCESS_CONTACT_COLD_LEADS} />
  );
}

export function CallCenterContactProcessSurveysDetailPage() {
  return (
    <CallCenterView type={PageType.PROCESS_CONTACT_SURVEYS} />
  );
}

export function CallCenterContactDetailPage() {
  return (
    <CallCenterView type={PageType.DETAIL} />
  );
}

export default CallCenterView;
